/**
 * Messy-file ingest analyzer
 *
 * Turns an arbitrarily-formatted source sheet into a canonical-keyed data set
 * the profile transformers already understand (row.unit, row.account, row.jan…).
 *
 * It does three things the naive first-row reader can't:
 *   1. Finds the real header row when title/blank/metadata rows sit above it.
 *   2. Fuzzy-matches odd column names ("Acct No.", "GL Account") to MV fields.
 *   3. Cleans data: drops blank/subtotal rows and coerces text amounts
 *      ($1,000, (500), 1000-) to numbers.
 *
 * Flow: buildPlan() proposes a mapping → the UI lets the user override it →
 * applyPlan() extracts the final canonical rows for the transform engine.
 */
import * as XLSX from 'xlsx';
import { PROFILES } from '../profiles/index';

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

// Fields whose values are numeric and should be coerced from messy text.
const AMOUNT_FIELDS = new Set([...MONTHS, 'debit', 'credit', 'amount']);

// Canonical field → known header variants. Superset of the older alias tables.
const ALIASES = {
  unit: ['unit', 'unit_id', 'business_unit', 'business_unit_id', 'bu', 'cost_center', 'costcenter', 'dept', 'department'],
  account: ['account', 'account_no', 'account_number', 'acct', 'acct_no', 'gl_account', 'gl_acct', 'gl', 'account_#'],
  company_id: ['company_id', 'company', 'companyid', 'company_no', 'entity', 'co', 'co_id'],
  accounting_date: ['accounting_date', 'acct_date', 'date', 'gl_date', 'posting_date', 'trans_date', 'period_date'],
  description: ['description', 'remarks', 'desc', 'memo', 'notes', 'detail', 'line_description'],
  debit: ['debit', 'dr', 'debit_amount', 'debit_amt'],
  credit: ['credit', 'cr', 'credit_amount', 'credit_amt'],
  amount: ['amount', 'amt', 'net_amount', 'value', 'total'],
  project_type: ['project_type', 'projtype', 'proj_type', 'ptype'],
  project_id: ['project_id', 'project', 'proj_id', 'projid', 'project_no'],
  data_type: ['data_type', 'datatype', 'dtype'],
  remarks: ['remarks', 'remark', 'comment', 'comments'],
  entry_type: ['entry_type', 'entrytype', 'type'],
};

export function normalizeHeader(key) {
  return String(key ?? '').trim().toLowerCase().replace(/\s+/g, '_');
}

/**
 * Coerce a messy amount cell to a number.
 * Handles $, thousands commas, (parens) negatives, and trailing-minus.
 * Returns a number, or null when the cell isn't numeric.
 */
export function coerceNumber(v) {
  if (typeof v === 'number') return v;
  if (v == null) return null;
  let s = String(v).trim();
  if (s === '') return null;
  let sign = 1;
  if (/^\(.*\)$/.test(s)) { sign = -1; s = s.slice(1, -1).trim(); }
  s = s.replace(/[$\s,]/g, '');
  if (/-$/.test(s)) { sign *= -1; s = s.replace(/-$/, ''); }
  if (/^-/.test(s)) { sign *= -1; s = s.slice(1); }
  if (s === '' || isNaN(Number(s))) return null;
  return sign * Number(s);
}

// Levenshtein-based similarity in [0,1].
function similarity(a, b) {
  if (a === b) return 1;
  const m = a.length;
  const n = b.length;
  if (m === 0 || n === 0) return 0;
  const d = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
    }
  }
  return 1 - d[m][n] / Math.max(m, n);
}

/**
 * Resolve a raw header to a canonical field within the profile's field list.
 * Returns { canonical, confidence } where confidence is exact|alias|fuzzy, or null.
 */
export function resolveHeader(header, fields) {
  const norm = normalizeHeader(header);
  if (!norm) return null;

  // Exact canonical name.
  if (fields.includes(norm)) return { canonical: norm, confidence: 'exact' };

  // Month columns like "JAN-26" / "January" when the profile expects months.
  const monthPrefix = norm.match(/^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/);
  if (monthPrefix && fields.includes(monthPrefix[1])) {
    return { canonical: monthPrefix[1], confidence: 'exact' };
  }

  // Known aliases.
  for (const field of fields) {
    if ((ALIASES[field] ?? []).includes(norm)) return { canonical: field, confidence: 'alias' };
  }

  // Fuzzy fallback against field names and their aliases.
  let best = null;
  for (const field of fields) {
    const variants = [field, ...(ALIASES[field] ?? [])];
    for (const v of variants) {
      let score = similarity(norm, v);
      // Reward substring containment (e.g. "gl_account_number" ⊇ "account").
      if (norm.includes(v) || v.includes(norm)) score = Math.max(score, 0.85);
      if (!best || score > best.score) best = { canonical: field, score };
    }
  }
  if (best && best.score >= 0.72) return { canonical: best.canonical, confidence: 'fuzzy' };
  return null;
}

function canonicalFieldsFor(profileKey) {
  const p = PROFILES[profileKey];
  return [...(p.sourceColumns ?? []), ...(p.optionalColumns ?? [])];
}

function sheetToAoa(wb, sheetName) {
  const sheet = wb.Sheets[sheetName];
  // raw:false lets SheetJS format dates; defval keeps column positions aligned.
  return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', blankrows: true });
}

/**
 * Score how well a given row works as the header row.
 * Higher when more canonical fields (especially required ones) resolve.
 */
function scoreHeaderRow(cells, fields, requiredFields) {
  const matched = new Set();
  let requiredMatched = 0;
  cells.forEach((cell) => {
    const res = resolveHeader(cell, fields);
    if (res && !matched.has(res.canonical)) {
      matched.add(res.canonical);
      if (requiredFields.includes(res.canonical)) requiredMatched++;
    }
  });
  return matched.size + requiredMatched * 2;
}

function detectHeaderRow(aoa, fields, requiredFields) {
  const limit = Math.min(aoa.length, 15);
  let best = { index: 0, score: -1 };
  for (let i = 0; i < limit; i++) {
    const cells = aoa[i] ?? [];
    if (cells.every((c) => String(c ?? '').trim() === '')) continue;
    const score = scoreHeaderRow(cells, fields, requiredFields);
    // Require at least one data row beneath a candidate header.
    const hasDataBelow = aoa.slice(i + 1).some((r) => r.some((c) => String(c ?? '').trim() !== ''));
    if (score > best.score && hasDataBelow) best = { index: i, score };
  }
  return best.index < 0 ? 0 : best.index;
}

const TOTAL_MARKER = /\b(sub\s*total|subtotals?|grand\s*total|totals?)\b/;

function looksLikeJunkRow(obj, fields) {
  const values = fields.map((f) => obj[f]);
  const allEmpty = values.every((v) => v === '' || v == null);
  if (allEmpty) return true;
  // Subtotal / grand-total rows: account is required on real data rows, so an
  // empty account paired with a "total" marker in any cell is a summary line.
  const acct = String(obj.account ?? '').trim();
  if (acct === '') {
    const text = values.map((v) => String(v ?? '').toLowerCase()).join(' ');
    if (TOTAL_MARKER.test(text)) return true;
  }
  return false;
}

/**
 * Extract canonical rows given a plan (sheet, header row, column mapping).
 * Applies amount coercion and drops junk rows. Returns { data, dropped }.
 */
function extract(wb, plan) {
  const aoa = sheetToAoa(wb, plan.sheetName);
  const dataRows = aoa.slice(plan.headerRowIndex + 1);
  const mapped = plan.columns.filter((c) => c.canonical);

  const data = [];
  let dropped = 0;

  dataRows.forEach((row) => {
    const obj = {};
    mapped.forEach((col) => {
      let val = row[col.index];
      if (AMOUNT_FIELDS.has(col.canonical)) {
        const n = coerceNumber(val);
        val = n === null ? (String(val ?? '').trim() === '' ? '' : val) : n;
      } else {
        val = typeof val === 'string' ? val.trim() : val;
      }
      // First non-empty wins if two columns map to the same field.
      if (obj[col.canonical] === undefined || obj[col.canonical] === '' || obj[col.canonical] == null) {
        obj[col.canonical] = val;
      }
    });

    if (looksLikeJunkRow(obj, mapped.map((c) => c.canonical))) {
      dropped++;
      return;
    }
    data.push(obj);
  });

  return { data, dropped };
}

function deriveState(wb, plan, requiredFields) {
  const { data, dropped } = extract(wb, plan);
  const mappedFields = new Set(plan.columns.filter((c) => c.canonical).map((c) => c.canonical));
  const unmappedRequired = requiredFields.filter((f) => !mappedFields.has(f));
  return {
    ...plan,
    sample: data.slice(0, 5),
    rowCount: data.length,
    droppedRows: dropped,
    unmappedRequired,
  };
}

/**
 * Build an ingest plan for a workbook: pick the best sheet + header row,
 * auto-map columns, and compute a preview. Optionally pin sheet/header.
 */
export function buildPlan(wb, profileKey, opts = {}) {
  const fields = canonicalFieldsFor(profileKey);
  const requiredFields = PROFILES[profileKey].requiredFields ?? [];

  // Choose the sheet whose best header row scores highest.
  let sheetName = opts.sheetName;
  let headerRowIndex = opts.headerRowIndex;

  if (!sheetName) {
    let best = { name: wb.SheetNames[0], score: -1, header: 0 };
    for (const name of wb.SheetNames) {
      const aoa = sheetToAoa(wb, name);
      const header = detectHeaderRow(aoa, fields, requiredFields);
      const score = scoreHeaderRow(aoa[header] ?? [], fields, requiredFields);
      if (score > best.score) best = { name, score, header };
    }
    sheetName = best.name;
    if (headerRowIndex == null) headerRowIndex = best.header;
  }

  const aoa = sheetToAoa(wb, sheetName);
  if (headerRowIndex == null) headerRowIndex = detectHeaderRow(aoa, fields, requiredFields);

  const headerCells = aoa[headerRowIndex] ?? [];
  const columns = headerCells
    .map((cell, index) => ({ cell, index }))
    .filter(({ cell }) => String(cell ?? '').trim() !== '')
    .map(({ cell, index }) => {
      const res = resolveHeader(cell, fields);
      return {
        index,
        sourceHeader: String(cell).trim(),
        canonical: res ? res.canonical : null,
        confidence: res ? res.confidence : null,
      };
    });

  const plan = {
    profileKey,
    sheetName,
    sheetNames: wb.SheetNames,
    headerRowIndex,
    columns,
    canonicalFields: fields,
    requiredFields,
  };

  return deriveState(wb, plan, requiredFields);
}

/**
 * Override a single column's mapping (canonical field name, or null to ignore)
 * and recompute the preview.
 */
export function setColumnMapping(wb, plan, columnIndex, canonical) {
  const columns = plan.columns.map((c) =>
    c.index === columnIndex ? { ...c, canonical: canonical || null, confidence: canonical ? 'manual' : null } : c
  );
  return deriveState(wb, { ...plan, columns }, plan.requiredFields);
}

/** Rebuild the plan for a different sheet or header row (re-auto-maps columns). */
export function rebuildPlan(wb, plan, { sheetName, headerRowIndex } = {}) {
  return buildPlan(wb, plan.profileKey, {
    sheetName: sheetName ?? plan.sheetName,
    headerRowIndex: headerRowIndex != null ? headerRowIndex : (sheetName ? null : plan.headerRowIndex),
  });
}

/** Final extraction consumed by the transform engine. */
export function applyPlan(wb, plan) {
  return extract(wb, plan).data;
}

/** First rows of the chosen sheet as raw arrays — used by the UI header picker. */
export function previewSheetRows(wb, sheetName, count = 12) {
  return sheetToAoa(wb, sheetName).slice(0, count);
}
